import { useEffect, useState } from "react";
import { GetUserQuery, useGetUserQuery } from "../graphql/types";
import { fetchAuthSession } from "aws-amplify/auth";

type AuthenticatedUserIdState =
  | { status: "loading" }
  | { status: "ready"; userId: string }
  | { status: "error"; error: Error };

export type AuthenticatedUserQueryState = {
  user: GetUserQuery["getUser"] | undefined;
  loading: boolean;
  error: Error | undefined;
};

const normalizeAuthError = ({ error }: { error: unknown }): Error =>
  error instanceof Error
    ? error
    : new Error("Unable to resolve the authenticated user");

export const useGetUserFromUserIdInAuthState =
  (): AuthenticatedUserQueryState => {
  const [authenticatedUserIdState, setAuthenticatedUserIdState] =
    useState<AuthenticatedUserIdState>({ status: "loading" });

  useEffect(() => {
    let isActive = true;

    const loadAuthenticatedUserId = async (): Promise<void> => {
      try {
        const authSession = await fetchAuthSession();
        const idTokenSubject = authSession.tokens?.idToken?.payload?.sub;
        const authenticatedUserId =
          authSession.userSub ||
          (typeof idTokenSubject === "string" ? idTokenSubject : undefined);

        if (!authenticatedUserId) {
          throw new Error("Authenticated user ID is unavailable");
        }

        if (isActive) {
          setAuthenticatedUserIdState({
            status: "ready",
            userId: authenticatedUserId,
          });
        }
      } catch (error) {
        if (isActive) {
          setAuthenticatedUserIdState({
            status: "error",
            error: normalizeAuthError({ error }),
          });
        }
      }
    };

    void loadAuthenticatedUserId();

    return () => {
      isActive = false;
    };
  }, []);

  const userId =
    authenticatedUserIdState.status === "ready"
      ? authenticatedUserIdState.userId
      : "";
  const { data, loading, error } = useGetUserQuery({
    variables: { userId },
    skip: authenticatedUserIdState.status !== "ready",
  });

  return {
    user: data?.getUser,
    loading:
      authenticatedUserIdState.status === "loading" ||
      (authenticatedUserIdState.status === "ready" && loading),
    error:
      authenticatedUserIdState.status === "error"
        ? authenticatedUserIdState.error
        : error,
  };
};

const useGetUserFromUserIdInAuth = (): GetUserQuery["getUser"] | undefined => {
  const { user } = useGetUserFromUserIdInAuthState();

  return user;
};

export default useGetUserFromUserIdInAuth;
